import { ArrowRight, ChevronRight, ArrowDownRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { serviceCategories, heroServiceCategories } from '../constants/data';

import cave from "../assets/fphc_cave.jpeg"

const Services = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const servicesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryId = params.get('section') || location.hash.replace('#', '');
    //console.log("DEBUG: URL Params: [" + params + "]")
    //console.log("DEBUG: URL Search Query - [" + categoryId + "]")
    
    if (categoryId) {
      setSelectedCategory(categoryId);
      //console.log("DEBUG: Selected Category" + setSelectedCategory(categoryId))
      
      // Wait for the content to render before scrolling
      setTimeout(() => {
        const element = document.getElementById(`service-section-${categoryId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 150);
    }
  }, [location]);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const contentVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: { opacity: 1, height: 'auto' }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[70vh]">
        <div className="absolute inset-0">
          <img 
            src={cave}
            alt="Environmental background"
            className="w-full h-full object-cover brightness-[0.4]"
          />
        </div>

        <div className="relative container mx-auto px-4 py-24 pt-32 pb-32 sm:pb-24">
          <div className="max-w-5xl mx-auto">
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-xl mb-8 animate-fade-in">
              <div className="flex gap-1">
                <div className="w-2.5 h-2.5 bg-[#75B2DD] rounded-[2px] animate-pulse-first" />
                <div className="w-2.5 h-2.5 bg-[#B5E2BF] rounded-[2px] animate-pulse-second" />
                <div className="w-2.5 h-2.5 bg-[#009E60] rounded-[2px] animate-pulse-third" />
              </div>
              <span className="text-success font-medium">Unsere Expertise</span>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
              <div className="space-y-8 animate-slide-up">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Innovative <span className="text-[#B5E2BF]">Umweltlösungen</span> für Ihre Herausforderungen
                </h1>
                <div className="w-20 h-1.5 bg-gradient-to-r from-success to-success/30 rounded-full" />
                <p className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed">
                  Von Abfallmanagement bis Wasserwirtschaft – <br className="hidden sm:block" />
                  Wir bieten maßgeschneiderte Lösungen für Ihren nachhaltigen Erfolg.
                </p>
                <div className="flex flex-col gap-4">
                  <Link
                    to="/contact"
                    className="w-full inline-flex items-center justify-center space-x-2 bg-success hover:bg-success/90 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl transition-all duration-300 group shadow-lg shadow-success/20 text-sm sm:text-base"
                  >
                    <span>Beratung anfordern</span>
                    <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 transform group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <a
                    href="#services-overview"
                    className="w-full inline-flex items-center justify-center space-x-2 bg-white/10 hover:bg-white/20 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl transition-all duration-300 group backdrop-blur-sm text-sm sm:text-base"
                  >
                    <span>Leistungen entdecken</span>
                    <ArrowDownRight className="w-4 sm:w-5 h-4 sm:h-5 transform group-hover:translate-x-1 group-hover:translate-y-1 transition-transform" />
                  </a>
                </div>
              </div>

              <div className="relative pl-0 lg:pl-12 mt-8 lg:mt-0">
                <div className="grid grid-cols-2 gap-4 sm:gap-6">
                  {heroServiceCategories.map((category, index) => (
                    <div
                      key={category.id}
                      className={`group bg-white/5 backdrop-blur-sm rounded-xl transition-all duration-300 overflow-hidden animate-fade-in ${
                        index % 2 === 0 ? 'lg:translate-y-8' : ''
                      }`}
                      style={{ animationDelay: `${index * 150}ms`, cursor: 'pointer' }}
                      onClick={() => navigate(`?section=${category.id}`)}
                    >
                      <div className="p-4 sm:p-6 h-full w-full transition-colors duration-300 hover:bg-white/10">
                        <h3 className="text-white font-semibold mb-2 text-sm sm:text-base">{category.title}</h3>
                        <p className="text-gray-400 text-xs sm:text-sm">{category.subtitle}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Diagonal Cut 
        <div className="absolute -bottom-1 left-0 w-full h-32 overflow-hidden">
          <div className="absolute bottom-0 left-0 w-full h-24 sm:h-32 bg-white"
               style={{
                 clipPath: 'polygon(0 100%, 100% 100%, 100% 0, 0 40%)'
               }}
          />
        </div>
        */}
      </section>

      {/* Services Section */}
      <section id="services-overview" ref={servicesRef} className="py-24">
        <div className="container mx-auto px-4">
          <motion.div 
            className="max-w-2xl mx-auto flex flex-col gap-6"
            variants={contentVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {serviceCategories.map((category) => {
              const Icon = category.icon;
              const isSelected = selectedCategory === category.id;
              const isHovered = hoveredCategory === category.id;
              
              return (
                <motion.div
                  key={category.id}
                  id={`service-section-${category.id}`}
                  variants={itemVariants}
                  className="relative w-full"
                  style={{ 
                    willChange: 'transform, opacity',
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden'
                  }}
                >
                  <motion.div
                    className={`
                      relative overflow-hidden rounded-2xl transition-all duration-150
                      ${isSelected ? 'bg-white shadow-2xl' : 'bg-white/50 hover:bg-white hover:shadow-xl'}
                    `}
                    onHoverStart={() => setHoveredCategory(category.id)}
                    onHoverEnd={() => setHoveredCategory(null)}
                    onClick={() => setSelectedCategory(isSelected ? null : category.id)}
                    whileHover={{ scale: 1.01 }}
                    transition={{ 
                      type: "tween", 
                      duration: 0.15,
                      ease: "easeOut"
                    }}
                    style={{
                      willChange: 'transform, opacity',
                      transform: 'translateZ(0)',
                      backfaceVisibility: 'hidden'
                    }}
                  >
                    <div className="relative z-10 p-6">
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center transform-gpu transition-colors duration-150"
                          style={{ 
                            backgroundColor: `${category.color}15`,
                            willChange: 'transform',
                            transform: 'translateZ(0)'
                          }}
                        >
                          <Icon 
                            className="w-6 h-6 transition-transform duration-150"
                            style={{ color: category.color }}
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold">{category.title}</h3>
                          <p className="text-gray-600 text-sm">{category.subtitle}</p>
                        </div>
                        <motion.div
                          animate={{ rotate: isSelected ? 90 : 0 }}
                          transition={{ 
                            type: "tween", 
                            duration: 0.15,
                            ease: "easeOut"
                          }}
                          style={{ willChange: 'transform' }}
                        >
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </motion.div>
                      </div>

                      <AnimatePresence mode="wait">
                        {isSelected && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ 
                              height: 'auto', 
                              opacity: 1,
                              transition: {
                                height: { duration: 0.2 },
                                opacity: { duration: 0.1, delay: 0.1 }
                              }
                            }}
                            exit={{ 
                              height: 0, 
                              opacity: 0,
                              transition: {
                                height: { duration: 0.2 },
                                opacity: { duration: 0.1 }
                              }
                            }}
                            className="mt-8"
                            style={{ 
                              willChange: 'transform, opacity',
                              transform: 'translateZ(0)',
                              backfaceVisibility: 'hidden'
                            }}
                          >
                            <div className="space-y-8">
                              {category.items.map((item, idx) => (
                                <motion.div
                                  key={item.title}
                                  variants={itemVariants}
                                  initial="hidden"
                                  animate="visible"
                                  transition={{ delay: idx * 0.1 }}
                                  className="space-y-4"
                                >
                                  <h4 className="text-xl font-semibold">{item.title}</h4>
                                  {item.description && (
                                    <p className="text-gray-600">{item.description}</p>
                                  )}
                                  <ul className="space-y-2">
                                    {item.details.map((detail, detailIdx) => (
                                      <motion.li
                                        key={detailIdx}
                                        variants={itemVariants}
                                        initial="hidden"
                                        animate="visible"
                                        transition={{ delay: (idx * 0.1) + (detailIdx * 0.05) }}
                                        className="flex items-start space-x-2"
                                      >
                                        <div 
                                          className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                                          style={{ backgroundColor: category.color }}
                                        />
                                        <span className="text-gray-600">{detail}</span>
                                      </motion.li>
                                    ))}
                                  </ul>
                                </motion.div>
                              ))}
                              
                              <motion.div
                                variants={itemVariants}
                                initial="hidden"
                                animate="visible"
                                transition={{ delay: 0.3 }}
                                className="pt-4"
                              >
                                <Link
                                  to="/contact"
                                  className="inline-flex items-center space-x-2 text-success hover:text-dark transition-colors group"
                                >
                                  <span>Beratung anfordern</span>
                                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                                </Link>
                              </motion.div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <div 
                      className="absolute inset-0 opacity-20 transition-opacity duration-150"
                      style={{
                        background: `linear-gradient(45deg, ${category.color}22, transparent)`,
                        willChange: 'opacity, transform',
                        transform: 'translateZ(0)',
                        backfaceVisibility: 'hidden'
                      }}
                    />
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;